import React, { useEffect, useState } from 'react';
import {useMutation, useQuery} from "@apollo/client";
import _ from 'lodash';
import Table from "../Table";
import {GridColDef} from "@mui/x-data-grid-pro";
import {ActionsMenu, FilteredSearch} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {AddOutlined, DeleteRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Button, Chip, Tooltip} from "@mui/material";
import {Box} from "@mui/system";
import {useResizeObserver} from "../../hooks/useResizeObserver";
import AddAttributeOptionValueModal from "./addOptionValueModal";
import {
    DeleteInstalledBaseNodeAttributeOptionValue
} from "../../graphql/mutations/deleteInstalledBaseNodeAttributeOptionValue";
import {InstalledBaseNodeAttributeOptionValueList} from "../../graphql/queries/installedBaseNodeAttributeOptionValueList";
import ConfirmDeleteModal from "../ConfirmDeleteModal";


interface Props {
    attributeId: string
}

const AttributeOptionValueList = ({attributeId}: Props) => {
    const {t} = useTranslation();

    const [addOptionValueModalOpen, setAddOptionValueModalOpen] = React.useState(false);
    const [deleteOptionValue, deleteOptionValueResult] = useMutation(DeleteInstalledBaseNodeAttributeOptionValue)
    const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string } | null>(null);

    const [searchQuery, setSearchQuery] = useState('')

    const [ref, toolbarHeight] = useResizeObserver();
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight = viewportHeight
        - toolbarHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 63 // Header
        - 38 // Breadcrumbs incl. margin
        - 57 // paddings: configurations layout (12) + content (12+12) + toolbar (8+13, excluded from contentRect)

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const attributeOptionValueListQuery = useQuery(InstalledBaseNodeAttributeOptionValueList, {
        variables: {
            id: attributeId,
            // Do NOT pass search.fields here — the installedBase subgraph 500s on explicit
            // fields. The default search covers label and value.
            search: searchQuery ? {query: searchQuery} : undefined,
        }
    })
    const optionValueListData = _.get(attributeOptionValueListQuery, 'data.installedBaseNodePropertyOptionValueList.optionValues', [])

    const onDeleteOptionValue = () => {
        if (!deleteTarget) return;
        deleteOptionValue({
            variables: {
                id: deleteTarget.id
            },
            refetchQueries: ["InstalledBaseNodeAttributeOptionValue", "InstalledBaseNodeAttributeOptionValueList"]
        })
            .then((res) => {
                if (res.data?.deleteInstalledBaseNodePropertyOptionValue?.success && res.data?.deleteInstalledBaseNodePropertyOptionValue?.message) {
                    toast.success(res.data.deleteInstalledBaseNodePropertyOptionValue.message);
                }
                setDeleteTarget(null);
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t))
                setDeleteTarget(null);
            })
    }

    const columns: GridColDef[] = [
        {
            field: 'label',
            headerName: t('Label'),
            flex: 1,
        },
        {
            field: 'value',
            headerName: t('Value'),
            flex: 1,
        },
        {
            field: 'parentValues',
            headerName: t('Parent option values'),
            flex: 1,
            disableColumnMenu: true,
            renderCell: (params) => {
                const parentOptionValues = _.get(params.row, 'parentOptionValues', []);
                const parentValuesCount = parentOptionValues.length;

                // No parent values - show dash
                if (parentValuesCount === 0) {
                    return '-';
                }

                // Single parent value - show the value
                if (parentValuesCount === 1) {
                    return _.get(parentOptionValues[0], 'value', '');
                }

                // Multiple parent values - show first + badge with tooltip
                if (parentValuesCount > 1) {
                    const firstValue = _.get(parentOptionValues[0], 'value', '');
                    const allValuesText = parentOptionValues
                        .map((pov: any) => _.get(pov, 'value', ''))
                        .filter((v: string) => v)
                        .join(', ');

                    return (
                        <Tooltip
                            title={allValuesText}
                            placement="top-start"
                            enterDelay={700}
                            enterNextDelay={500}
                        >
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {firstValue}
                                </Box>
                                <Chip
                                    label={`+${parentValuesCount - 1}`}
                                    size="small"
                                    sx={{ backgroundColor: '#999', color: 'white', fontWeight: 'bold', flexShrink: 0 }}
                                />
                            </Box>
                        </Tooltip>
                    );
                }

                return null;
            },
        },
        {
            field: 'actions',
            headerName: t('Actions'),
            width: 80,
            disableColumnMenu: true,
            align: "center",
            renderCell: (params) => {
                const optionValueId = _.get(params, 'row.id')
                const optionValueName = _.get(params, 'row.label') || _.get(params, 'row.value')

                const actionItems: { item: React.ReactNode, onClick: () => void, active: boolean }[] = [
                    {
                        item: <><DeleteRounded fontSize={'inherit'} sx={{mr: '4px'}}/>{t('Delete')}</>,
                        onClick: () => setDeleteTarget({id: optionValueId, name: optionValueName}),
                        active: true,
                    },
                ]

                return <ActionsMenu items={actionItems} />
            }
        }
    ];

    const onDebouncedSearch = _.debounce((values) => {
        setSearchQuery(values.query);
    }, 700);

    return (
        <>
            <div
                ref={ref}
                className={'flex-row'}
                style={{justifyContent: 'space-between', alignItems: 'center', padding: '8px 8px 13px 8px'}}
            >
                <FilteredSearch
                    onSearch={onDebouncedSearch}
                    defaultFields={[]}
                />
                <Button
                    variant="contained"
                    startIcon={<AddOutlined />}
                    onClick={() => setAddOptionValueModalOpen(true)}
                >
                    {t('Add')}
                </Button>
            </div>
            <div style={{width: '100%', height: tableHeight}}>
                <Table
                    data={optionValueListData}
                    columns={columns}
                    disableColumnReorder
                    disableColumnPinning
                    disableColumnMenu
                    disableColumnSorting
                    disableColumnResize
                    disableRowSelectionOnClick
                />
            </div>
            {addOptionValueModalOpen && <AddAttributeOptionValueModal
                attributeId={attributeId}
                open={addOptionValueModalOpen}
                onClose={() => setAddOptionValueModalOpen(false)}
            />}
            {deleteTarget && (
                <ConfirmDeleteModal
                    open={!!deleteTarget}
                    title={t('Delete option value')}
                    message={t('Are you sure you want to delete the option value {{name}}? This action cannot be undone.', {name: deleteTarget.name})}
                    loading={deleteOptionValueResult.loading}
                    onConfirm={onDeleteOptionValue}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
        </>

    );
}

export default AttributeOptionValueList;
